import React, { useRef, useState } from 'react';
import { rsvp } from '../rsvp';
import styles from './RsvpForm.module.css';

/**
 * RsvpForm — meetup RSVP capture (RSVP v1).
 *
 * Same interaction contract as NewsletterForm:
 *   - synchronous double-submit guard via ref (D-08)
 *   - client-side validation before any fetch (D-06)
 *   - hidden _gotcha honeypot forwarded to FormCarry (BACK-02)
 *   - aria-live status region announces every state change (NEWS-03)
 *
 * The event prop discriminates which meetup the RSVP belongs to in the
 * shared FormCarry submission log (see rsvp.ts).
 */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormStatus = 'idle' | 'loading' | 'success' | 'validationError' | 'serverError';

interface RsvpFormProps {
  event: string; // e.g. '2026-09-10'
}

export default function RsvpForm({ event }: RsvpFormProps): React.ReactElement {
  const [status, setStatus] = useState<FormStatus>('idle');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const submittingRef = useRef(false);
  const gotchaRef = useRef<HTMLInputElement>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (submittingRef.current) return;
    submittingRef.current = true;

    if (!name.trim()) {
      setStatus('validationError');
      setStatusMessage('✕ Tell us your name so we know who to look for.');
      submittingRef.current = false;
      return;
    }

    if (!email || !EMAIL_REGEX.test(email)) {
      setStatus('validationError');
      setStatusMessage('✕ Please enter a valid email address.');
      submittingRef.current = false;
      return;
    }

    setStatus('loading');
    setStatusMessage('Sending…');

    const gotcha = gotchaRef.current?.value ?? '';

    try {
      const result = await rsvp(name.trim(), email, event, gotcha);

      if (result.kind === 'success') {
        setStatus('success');
        setStatusMessage('✓ SEE YOU THERE.');
      } else if (result.kind === 'validation') {
        setStatus('validationError');
        setStatusMessage(`✕ ${result.message || 'Check your email address.'}`);
      } else {
        setStatus('serverError');
        setStatusMessage('✕ Something went wrong. Email hello@oakcliff.tech');
      }
    } catch {
      setStatus('serverError');
      setStatusMessage('✕ Something went wrong. Email hello@oakcliff.tech');
    } finally {
      submittingRef.current = false;
    }
  };

  const isLoading = status === 'loading';
  const isError = status === 'validationError' || status === 'serverError';
  const isSuccess = status === 'success';

  const statusClass =
    isSuccess
      ? `${styles.status} ${styles.statusSuccess}`
      : isError
      ? `${styles.status} ${styles.statusError}`
      : styles.status;

  return (
    <form className={styles.form} onSubmit={handleSubmit} noValidate>
      <label htmlFor="rsvp-name" className={styles.label}>
        Your Name
      </label>
      <input
        id="rsvp-name"
        type="text"
        name="name"
        className={isError ? `${styles.input} ${styles.inputError}` : styles.input}
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="First name is plenty"
        autoComplete="name"
        disabled={isLoading}
        aria-describedby="rsvp-status"
      />

      <label htmlFor="rsvp-email" className={styles.label}>
        Your Email
      </label>
      <div className={styles.inputRow}>
        <input
          id="rsvp-email"
          type="email"
          name="email"
          className={isError ? `${styles.input} ${styles.inputError}` : styles.input}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          autoComplete="email"
          disabled={isLoading}
          aria-describedby="rsvp-status"
        />
        <button type="submit" className={styles.button} disabled={isLoading}>
          {isLoading ? 'SENDING…' : "I'M COMING"}
        </button>
      </div>

      {/* Honeypot — hidden from humans and the accessibility tree (BACK-02) */}
      <input
        type="text"
        name="_gotcha"
        ref={gotchaRef}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{ display: 'none' }}
      />

      <div
        id="rsvp-status"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className={statusClass}
      >
        {statusMessage}
      </div>

      {status === 'idle' && (
        <p className={styles.hint}>
          A headcount helps us hold the tables. No spam, ever.
        </p>
      )}
    </form>
  );
}
